import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, ArrowLeft, Sparkles, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { useBooking } from '../context/BookingContext';
import { getLocalLander } from '../data/localLandersData';

const PHONE_DISPLAY = '(626) 737-3183';
const PHONE_HREF = 'tel:+16267373183';
const ADDRESS_LINES = ['506 N Garfield Ave, Ste 210', 'Alhambra, CA 91801'];

const LocalServiceLanderPage = ({ landerSlug }) => {
  const params = useParams();
  const slug = landerSlug || params.slug;
  const lander = getLocalLander(slug);
  const { openBooking } = useBooking();

  if (!lander) return <Navigate to="/services" replace />;

  const path = `/services/${lander.slug}`;
  const canonical = `https://akirasignaturecare.com${path}`;

  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: lander.primaryKeyword,
    alternateName: lander.h1,
    description: lander.metaDescription,
    url: canonical,
    procedureType: 'https://schema.org/NoninvasiveProcedure',
    performer: { '@id': 'https://akirasignaturecare.com/#clinic' },
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://akirasignaturecare.com/services' },
      { '@type': 'ListItem', position: 3, name: lander.h1, item: canonical },
    ],
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: lander.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`lander-${lander.slug}`}>
      <SEO
        title={lander.title}
        description={lander.metaDescription}
        path={path}
        image={lander.hero.image}
        schema={[procedureSchema, breadcrumb, faqSchema]}
      />

      {/* Hero */}
      <section
        className="relative text-white pt-32 pb-24"
        style={{
          backgroundImage: `linear-gradient(rgba(28, 25, 23, 0.72), rgba(28, 25, 23, 0.55)), url(${lander.hero.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-6 max-w-5xl">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors text-sm"
            data-testid="lander-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="text-rose-300 text-xs tracking-[0.35em] uppercase mb-4">
            {lander.hero.eyebrow}
          </div>
          <h1
            className="font-serif text-4xl lg:text-6xl mb-5 leading-tight max-w-3xl"
            data-testid="lander-h1"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
          >
            {lander.h1}
          </h1>
          <p className="text-lg text-stone-200 leading-relaxed font-light max-w-2xl">
            {lander.hero.subhead}
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <button
              type="button"
              onClick={() => openBooking(lander.primaryKeyword)}
              data-testid="lander-book-btn"
              className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white text-sm tracking-[0.2em] uppercase px-8 py-3.5 transition-colors"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={PHONE_HREF}
              data-testid="lander-call-btn"
              className="inline-flex items-center gap-2 border border-white/70 hover:bg-white hover:text-stone-900 text-white text-sm tracking-[0.2em] uppercase px-8 py-3.5 transition-colors"
            >
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Who + Why physician-led */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl grid md:grid-cols-2 gap-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-rose-400" />
              <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
                {lander.who.title}
              </span>
            </div>
            <p className="text-stone-700 text-lg leading-relaxed font-light">
              {lander.who.body}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5 text-rose-500" />
              <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
                Why physician-led
              </span>
            </div>
            <p className="text-stone-700 text-lg leading-relaxed font-light">
              {lander.whyPhysicianLed}
            </p>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-20 bg-stone-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
              Your visit
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-10 leading-snug">
            What to expect
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {lander.whatToExpect.map((s, i) => (
              <div key={i} className="bg-white p-8 shadow-sm">
                <div className="text-rose-500 font-serif text-4xl mb-3">0{i + 1}</div>
                <div className="font-serif text-xl text-stone-900 mb-3">{s.step}</div>
                <p className="text-stone-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related treatments */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
              Explore related
            </span>
          </div>
          <h2 className="font-serif text-3xl text-stone-900 mb-8">
            Frequently paired treatments
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {lander.related.map((r) => (
              <Link
                key={r.slug}
                to={r.slug}
                data-testid={`lander-related-${r.slug}`}
                className="group flex items-center justify-between border border-stone-200 hover:border-rose-400 p-6 bg-white transition-colors"
              >
                <span className="text-stone-800 group-hover:text-rose-500 transition-colors">
                  {r.name}
                </span>
                <ArrowRight className="w-4 h-4 text-rose-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Serving nearby */}
      <section className="py-16 bg-stone-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-rose-500" />
            <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
              Serving nearby communities
            </span>
          </div>
          <p className="text-stone-700 text-lg font-light leading-relaxed">
            Our Alhambra clinic welcomes patients from{' '}
            {lander.servingNearby.map((c, i) => (
              <span key={c}>
                <span className="font-medium text-stone-900">{c}</span>
                {i < lander.servingNearby.length - 1 ? ', ' : ''}
              </span>
            ))}
            {' '}and the wider San Gabriel Valley.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-rose-500" />
            <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
              Common questions
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-10 leading-snug">
            {lander.primaryKeyword} — patient FAQs
          </h2>
          <div className="space-y-6">
            {lander.faqs.map((f, i) => (
              <details key={i} className="group border-b border-stone-200 pb-5">
                <summary className="flex items-center justify-between cursor-pointer text-lg font-medium text-stone-900 hover:text-rose-500 transition-colors">
                  <span>{f.q}</span>
                  <span className="text-rose-500 text-2xl group-open:rotate-45 transition-transform inline-block">+</span>
                </summary>
                <p className="mt-3 text-stone-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA + NAP */}
      <section className="py-20 bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="font-serif text-3xl md:text-5xl mb-6 leading-tight">
            Ready when you are.
          </h2>
          <p className="text-stone-300 text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10">
            Book a physician-led consultation at our Alhambra clinic or call the team directly.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-10">
            <button
              type="button"
              onClick={() => openBooking(lander.primaryKeyword)}
              data-testid="lander-book-btn-footer"
              className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white text-sm tracking-[0.2em] uppercase px-8 py-3.5 transition-colors"
            >
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 border border-white/70 hover:bg-white hover:text-stone-900 text-white text-sm tracking-[0.2em] uppercase px-8 py-3.5 transition-colors"
            >
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
          </div>
          <div className="text-stone-300 text-sm">
            <div className="uppercase tracking-[0.3em] text-[10px] text-rose-300 mb-2">Akira Signature Care</div>
            {ADDRESS_LINES.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocalServiceLanderPage;
